import { useState, useEffect } from 'react';
import { Mail, Eye, Trash2, RefreshCw, CheckCircle, Clock, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import adminApi from '@/lib/adminApi';

const MessagesManager = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchMessages = async () => {
    try {
      const response = await adminApi.get(`/contact/submissions`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const markAsRead = async (message) => {
    if (message.is_read) return;
    try {
      await adminApi.put(`/contact/submissions/${message.id}/read`, {});
      fetchMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    markAsRead(message);
  };

  const handleDelete = async () => {
    if (!messageToDelete) return;
    try {
      await adminApi.delete(`/contact/submissions/${messageToDelete.id}`);
      toast.success('Message supprimé');
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
      if (selectedMessage?.id === messageToDelete.id) {
        setSelectedMessage(null);
      }
      fetchMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const exportMessages = () => {
    const csv = [
      ['Date', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Organisation', 'Objet', 'Message', 'Lu'].join(';'),
      ...messages.map(m => [
        new Date(m.created_at).toLocaleDateString('fr-FR'),
        m.nom,
        m.prenom,
        m.email,
        m.telephone || '',
        m.organisation || '',
        m.objet,
        m.message.replace(/;/g, ',').replace(/\n/g, ' '),
        m.is_read ? 'Oui' : 'Non'
      ].join(';'))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `messages_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Export téléchargé');
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !m.is_read;
    if (filter === 'read') return m.is_read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.is_read).length;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a8a]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="messages-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-playfair text-2xl font-semibold text-[#0F172A]">
            Messages reçus
          </h2>
          <p className="text-slate-600 font-inter text-sm mt-1">
            {messages.length} messages • {unreadCount} non lu(s)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={exportMessages}
          disabled={messages.length === 0}
          data-testid="export-messages-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Exporter CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { value: 'all', label: 'Tous' },
          { value: 'unread', label: `Non lus (${unreadCount})` },
          { value: 'read', label: 'Lus' }
        ].map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
            className={filter === f.value ? 'bg-[#1e3a8a]' : ''}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Messages List */}
      <Card>
        <CardContent className="p-0">
          {filteredMessages.length === 0 ? (
            <div className="p-8 text-center text-slate-500 font-inter">
              <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun message</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                    !message.is_read ? 'bg-blue-50/50' : ''
                  }`}
                  onClick={() => openMessage(message)}
                  data-testid={`message-row-${message.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.is_read ? 'bg-slate-100' : 'bg-[#1e3a8a]'
                  }`}>
                    {message.is_read ? (
                      <CheckCircle className="w-5 h-5 text-slate-400" />
                    ) : (
                      <Mail className="w-5 h-5 text-white" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-inter font-medium truncate ${
                        message.is_read ? 'text-slate-600' : 'text-[#0F172A]'
                      }`}>
                        {message.prenom} {message.nom}
                      </span>
                      {!message.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                    </div>
                    <p className={`text-sm truncate ${
                      message.is_read ? 'text-slate-500' : 'text-slate-700 font-medium'
                    }`}>
                      {message.objet}
                    </p>
                    <p className="text-xs text-slate-400 font-inter mt-1">
                      {formatDate(message.created_at)}
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMessageToDelete(message);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-message-btn-${message.id}`}
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Detail Dialog */}
      <Dialog open={!!selectedMessage} onOpenChange={() => setSelectedMessage(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle className="font-playfair">{selectedMessage?.objet}</DialogTitle>
          </DialogHeader>

          {selectedMessage && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500 font-inter">De :</span>
                  <p className="font-inter font-medium">
                    {selectedMessage.prenom} {selectedMessage.nom}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500 font-inter">Email :</span>
                  <p className="font-inter">
                    <a href={`mailto:${selectedMessage.email}`} className="text-[#1e3a8a] hover:underline">
                      {selectedMessage.email}
                    </a>
                  </p>
                </div>
                {selectedMessage.telephone && (
                  <div>
                    <span className="text-slate-500 font-inter">Téléphone :</span>
                    <p className="font-inter">{selectedMessage.telephone}</p>
                  </div>
                )}
                {selectedMessage.organisation && (
                  <div>
                    <span className="text-slate-500 font-inter">Organisation :</span>
                    <p className="font-inter">{selectedMessage.organisation}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-500 font-inter">Date :</span>
                  <p className="font-inter">{formatDate(selectedMessage.created_at)}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <span className="text-slate-500 font-inter text-sm">Message :</span>
                <p className="font-inter mt-2 whitespace-pre-wrap">
                  {selectedMessage.message}
                </p>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.objet}`}
                  className="bg-[#1e3a8a] hover:bg-[#172554]"
                  data-testid="reply-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Répondre
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setMessageToDelete(selectedMessage);
                    setDeleteDialogOpen(true);
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le message sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-message-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MessagesManager;
